/**
 * RuleSet type (§4.1) and rule helpers that depend only on rule data.
 */
import type { FoulType } from '../types';

/** Known preset ids; custom/league presets may use any other string. */
export type RuleSetId = 'fiba' | 'custom' | (string & {});

/**
 * Timeout allocation.
 * - perHalf: FIBA style. "First half" = periods 1..ceil(periods / 2).
 *   Unused timeouts do not carry from one half to the next, nor into overtime.
 * - perGame: NBA style. One pool for all regulation periods.
 * - perPeriod: a fresh allocation every regulation period (custom leagues).
 * In every kind, each overtime period gets a fresh `perOvertime` allocation;
 * unused regulation/overtime timeouts never carry into a later overtime.
 */
export type TimeoutRule =
  | {
      kind: 'perHalf';
      firstHalf: number;
      secondHalf: number;
      perOvertime: number;
      /** Most timeouts a team may take at 2:00 or less in the last regulation period. */
      lastTwoMinutesMax?: number;
    }
  | { kind: 'perGame'; perGame: number; perOvertime: number }
  | { kind: 'perPeriod'; perPeriod: number; perOvertime: number };

export interface ShotClockRule {
  enabled: boolean;
  /** Full reset value in seconds (24). */
  fullSec: number;
  /** Short reset value in seconds (14). */
  resetSec: number;
  /**
   * A made field goal (or the last free throw) resets the shot clock to `fullSec` and gives
   * the ball to the other team. Missing in games saved before v3: treated as false.
   */
  resetOnScore?: boolean;
  /**
   * After that reset the shot clock waits at `fullSec` until the throw-in is touched on the
   * court (FIBA). Missing in older games: it counts straight away.
   */
  holdAfterScore?: boolean;
}

/** When the game clock stops by itself. Missing in older games: it never does. */
export interface ClockStopRule {
  /** After a field goal, in the last N seconds of periods before the last regulation one. */
  afterBasketOtherPeriodsSec: number;
  /** After a field goal, in the last N seconds of the last regulation period and overtime. */
  afterBasketLastPeriodsSec: number;
  /** A foul stops the clock (a throw-in restarts it after 5 s; free throws wait for the scorer). */
  onFoul: boolean;
}

/** Length of each kind of break, in seconds. */
export interface BreakRule {
  timeoutSec: number;
  /** Between quarters (or other regulation periods that are not half-time). */
  periodBreakSec: number;
  halfTimeSec: number;
  /** Before each overtime period. */
  overtimeBreakSec: number;
}

export type BreakKind = 'timeout' | 'period' | 'half' | 'overtime';

/** Official FIBA lengths, also used when a stored rule set has no `breaks` (pre-v3 games). */
export const FIBA_BREAKS: Readonly<BreakRule> = Object.freeze({
  timeoutSec: 60,
  periodBreakSec: 120,
  halfTimeSec: 900,
  overtimeBreakSec: 120,
});

export interface RuleSet {
  id: RuleSetId;
  periods: number;
  periodLengthSec: number;
  overtimeLengthSec: number;
  /** Team fouls in a regulation period from which free throws are awarded (5 = from the 5th foul). */
  teamFoulPenaltyAt: number;
  /** Same threshold for overtime periods. */
  overtimeTeamFoulPenaltyAt: number;
  /**
   * When true (FIBA), fouls in overtime are counted together with the fouls of the last
   * regulation period (all overtimes are treated as an extension of it).
   * When false (NBA), every overtime period starts at zero fouls.
   */
  overtimeFoulsCarryOver: boolean;
  /** Player foul limit: a player with this many counted fouls has fouled out (FIBA 5, NBA 6). */
  personalFoulLimit: number;
  /**
   * Player foul types that do NOT count as team fouls (NBA: technical, offensive).
   * Bench/coach fouls never count as team fouls.
   */
  teamFoulExcludedTypes: readonly FoulType[];
  /** Player foul types that do NOT count toward personalFoulLimit (NBA: technical). */
  personalFoulExcludedTypes: readonly FoulType[];
  /** A single foul of one of these types disqualifies (ejects) the player. */
  ejectionFoulTypes: readonly FoulType[];
  /**
   * The player is disqualified once the combined number of fouls of `types` reaches `count`
   * (FIBA: 2 technical/unsportsmanlike in any mix; NBA: 2 technicals, 2 flagrant 1).
   */
  ejectionCombos: readonly EjectionCombo[];
  timeouts: TimeoutRule;
  shotClock: ShotClockRule;
  /** Break timer lengths. Missing in games saved before v3: FIBA_BREAKS apply. */
  breaks?: BreakRule;
  clockStops?: ClockStopRule;
}

export interface EjectionCombo {
  types: readonly FoulType[];
  count: number;
}

export function isOvertime(rules: RuleSet, periodNo: number): boolean {
  return periodNo > rules.periods;
}

/** Length of the given period in ms (regulation or overtime). Period 0 (pregame) uses period 1. */
export function periodLengthMs(rules: RuleSet, periodNo: number): number {
  return (isOvertime(rules, periodNo) ? rules.overtimeLengthSec : rules.periodLengthSec) * 1000;
}

/** Last period number of the first half for `perHalf` timeouts. */
export function firstHalfLastPeriod(rules: RuleSet): number {
  return Math.ceil(rules.periods / 2);
}

/** Team-foul penalty threshold that applies in the given period. */
export function teamFoulPenaltyAtFor(rules: RuleSet, periodNo: number): number {
  return isOvertime(rules, periodNo) ? rules.overtimeTeamFoulPenaltyAt : rules.teamFoulPenaltyAt;
}

/** Break lengths for `rules`, falling back to the FIBA values. */
export function breakLengths(rules: RuleSet): BreakRule {
  return { ...FIBA_BREAKS, ...(rules.breaks ?? {}) };
}

/** Length in ms of a break of `kind`. */
export function breakLengthMs(rules: RuleSet, kind: BreakKind): number {
  const b = breakLengths(rules);
  const sec =
    kind === 'timeout'
      ? b.timeoutSec
      : kind === 'half'
        ? b.halfTimeSec
        : kind === 'overtime'
          ? b.overtimeBreakSec
          : b.periodBreakSec;
  return sec * 1000;
}

/**
 * The break that follows the end of `periodNo`: half-time after the middle period of an even
 * number of periods, an overtime break before any overtime, otherwise a period break.
 */
export function breakAfterPeriod(rules: RuleSet, periodNo: number): BreakKind {
  if (periodNo >= rules.periods) return 'overtime';
  if (rules.periods % 2 === 0 && periodNo === rules.periods / 2) return 'half';
  return 'period';
}

/** Does a field goal with `remainingMs` on the clock stop the game clock? */
export function basketStopsClock(rules: RuleSet, periodNo: number, remainingMs: number): boolean {
  const c = rules.clockStops;
  if (c === undefined) return false;
  const sec =
    periodNo >= rules.periods ? c.afterBasketLastPeriodsSec : c.afterBasketOtherPeriodsSec;
  return remainingMs <= sec * 1000;
}

/** Does a foul stop the game clock? */
export function foulStopsClock(rules: RuleSet): boolean {
  return rules.clockStops?.onFoul === true;
}

/** Deep copy of a rule set (presets are frozen; callers get a mutable copy). */
export function cloneRules(rules: RuleSet): RuleSet {
  return {
    ...rules,
    timeouts: { ...rules.timeouts },
    shotClock: { ...rules.shotClock },
    ...(rules.breaks ? { breaks: { ...rules.breaks } } : {}),
    ...(rules.clockStops ? { clockStops: { ...rules.clockStops } } : {}),
    teamFoulExcludedTypes: [...rules.teamFoulExcludedTypes],
    personalFoulExcludedTypes: [...rules.personalFoulExcludedTypes],
    ejectionFoulTypes: [...rules.ejectionFoulTypes],
    ejectionCombos: rules.ejectionCombos.map((c) => ({ types: [...c.types], count: c.count })),
  };
}
